import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';

function PrivacyPolicy() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last Updated: December 26, 2025
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          1. Introduction
        </Typography>
        <Typography variant="body1" paragraph>
          Gmail Helper ("we", "our", or "the application") is committed to protecting your privacy. This Privacy Policy
          explains how we handle your data when you use our email sender management tool.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          2. Data We Access
        </Typography>
        <Typography variant="body1" paragraph>
          Gmail Helper requires access to your Gmail account to provide its services. Specifically, we access:
        </Typography>
        <Box component="ul" sx={{ mb: 2 }}>
          <Typography component="li" variant="body1">
            <strong>Email Metadata:</strong> Sender names, email addresses, and message counts
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Email Messages:</strong> To scan senders and perform bulk delete operations when requested
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Gmail Filters:</strong> To create, modify, and delete email filters on your behalf
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          3. Data Protection Mechanisms
        </Typography>
        <Typography variant="body1" paragraph>
          We implement the following data protection mechanisms to safeguard your sensitive information:
        </Typography>
        <Box component="ul" sx={{ mb: 2 }}>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>No Server-Side Storage:</strong> We do not store, transmit, or process any of your Gmail data on our servers.
            All data remains between your browser and Google's servers.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Local Storage Only:</strong> Sender lists and your selections are stored exclusively in your browser's local
            storage on your device. This data never leaves your computer.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Direct API Communication:</strong> All Gmail operations are performed directly between your browser and
            Google's Gmail API using OAuth 2.0 authentication. No intermediary servers process your data.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Encrypted Transmission:</strong> All communication with Gmail API is encrypted using HTTPS/TLS protocols
            to prevent interception.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>OAuth 2.0 Security:</strong> We use Google's OAuth 2.0 authentication framework, which means we never have
            access to your Google account password. You can revoke access at any time through your Google Account settings.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>No Third-Party Data Sharing:</strong> Your Gmail data is never shared with, sold to, or transmitted to any
            third parties. We do not have access to sell or share your data as it never reaches our servers.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 1 }}>
            <strong>Minimal Data Access:</strong> We only request the minimum Gmail API scopes necessary to provide the application's
            functionality (gmail.modify scope for reading and managing emails and filters).
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          4. How We Use Your Data
        </Typography>
        <Typography variant="body1" paragraph>
          Your Gmail data is used exclusively for the following purposes:
        </Typography>
        <Box component="ul" sx={{ mb: 2 }}>
          <Typography component="li" variant="body1">
            Analyzing email senders to display sender statistics
          </Typography>
          <Typography component="li" variant="body1">
            Creating Gmail filters based on your selections
          </Typography>
          <Typography component="li" variant="body1">
            Deleting emails from specific senders when you explicitly request it
          </Typography>
          <Typography component="li" variant="body1">
            Managing your Gmail filters (create, delete, sync)
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          All processing happens locally in your browser. We never analyze, mine, or use your data for any other purpose.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          5. Data Retention
        </Typography>
        <Typography variant="body1" paragraph>
          Since we do not collect or store your data on our servers, there is no data retention period on our end. Data stored
          in your browser's local storage persists until you:
        </Typography>
        <Box component="ul" sx={{ mb: 2 }}>
          <Typography component="li" variant="body1">
            Clear your browser's local storage/cookies
          </Typography>
          <Typography component="li" variant="body1">
            Sign out of Gmail Helper
          </Typography>
          <Typography component="li" variant="body1">
            Uninstall or stop using the application
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          6. Your Rights and Control
        </Typography>
        <Typography variant="body1" paragraph>
          You maintain complete control over your data:
        </Typography>
        <Box component="ul" sx={{ mb: 2 }}>
          <Typography component="li" variant="body1">
            <strong>Access Revocation:</strong> You can revoke Gmail Helper's access to your Gmail account at any time through
            Google Account settings (myaccount.google.com/permissions)
          </Typography>
          <Typography component="li" variant="body1">
            <strong>Data Deletion:</strong> You can clear locally stored data by signing out or clearing your browser storage
          </Typography>
          <Typography component="li" variant="body1">
            <strong>No Account Required:</strong> We do not create or maintain user accounts. Your Google account is used only
            for authentication via OAuth 2.0
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          7. Cookies and Tracking
        </Typography>
        <Typography variant="body1" paragraph>
          Gmail Helper uses browser local storage to save your sender selections locally on your device. We do not use cookies
          for tracking or analytics. Google AdSense (if displayed) may use cookies for ad personalization - please refer to
          Google's privacy policy for details on their advertising practices.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          8. Children's Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          Gmail Helper is not intended for use by children under the age of 13. We do not knowingly collect data from children.
          If you are under 13, please do not use this application.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          9. Security Measures
        </Typography>
        <Typography variant="body1" paragraph>
          While we implement security best practices (HTTPS encryption, OAuth 2.0, no server-side storage), no method of
          electronic transmission or storage is 100% secure. The security of your Gmail data is primarily protected by Google's
          security infrastructure, as all data processing happens directly between your browser and Google's servers.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          10. Changes to Privacy Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated"
          date. Continued use of Gmail Helper after changes constitutes acceptance of the updated policy.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          11. Google API Services User Data Policy
        </Typography>
        <Typography variant="body1" paragraph>
          Gmail Helper's use and transfer of information received from Google APIs adheres to the{' '}
          <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
            Google API Services User Data Policy
          </a>, including the Limited Use requirements. We only use the data to provide and improve the features described in
          this privacy policy.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          12. Contact Information
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions or concerns about this Privacy Policy, please contact us at:
        </Typography>
        <Typography variant="body1" paragraph>
          Email: privacy@jpmorgenthal.com<br />
          Website: https://jpmorgenthal.com/gmailhelper
        </Typography>

        <Box sx={{ mt: 4, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Summary:</strong> Gmail Helper does not store your Gmail data on any servers. All operations are performed
            locally in your browser, directly communicating with Google's Gmail API. You maintain full control over your data,
            and we never have access to, store, process, or share your email content or metadata on our infrastructure.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default PrivacyPolicy;
